/**
 * MessageList
 *
 * Renders messages with date separators and message grouping.
 * Consecutive messages from the same sender within 2 minutes
 * are grouped (no repeated avatar/name).
 */

import { isSameDay } from 'date-fns';
import DateSeparator from './DateSeparator';
import MessageBubble from './MessageBubble';
import SmartMessageBubble from './SmartMessageBubble';

const GROUP_THRESHOLD_MS = 120000; // 2 minutes

export default function MessageList({
  messages,
  currentUserId,
  isGroup,
  onReply,
  onForward,
  onEdit,
  onDeleteForMe,
  onDeleteForEveryone,
  onAddReaction,
  onRemoveReaction,
  editingMessage,
  editText,
  setEditText,
  onSaveEdit,
  onCancelEdit,
  activeMessageId,
  setActiveMessageId,
  currentRole,
  schoolId,
}) {
  const elements = [];

  // Sender-only docs (a smart message's inline copy in its source thread)
  // are never shown to non-senders — recipients get theirs in their Smart
  // Messages inbox.
  const visibleMessages = messages.filter(
    (m) => !(m.senderOnly === true && m.senderId !== currentUserId)
  );

  for (let i = 0; i < visibleMessages.length; i++) {
    const message = visibleMessages[i];
    const prevMessage = i > 0 ? visibleMessages[i - 1] : null;
    const messageDate = message.sentAt?.toDate?.() || new Date();
    const prevDate = prevMessage?.sentAt?.toDate?.() || null;

    // Date separator between different days
    if (!prevDate || !isSameDay(messageDate, prevDate)) {
      elements.push(
        <DateSeparator key={`date-${i}`} date={messageDate} />
      );
    }

    // Grouping: same sender within threshold, not system messages
    const isGrouped = prevMessage
      && prevMessage.senderId === message.senderId
      && prevMessage.type !== 'system'
      && message.type !== 'system'
      && prevDate
      && isSameDay(messageDate, prevDate)
      && (messageDate - prevDate) < GROUP_THRESHOLD_MS;

    const isOwn = message.senderId === currentUserId;
    const isEditing = editingMessage?.id === message.id;

    if (message.type === 'smart_message') {
      elements.push(
        <SmartMessageBubble
          key={message.id}
          message={message}
          isOwn={isOwn}
          currentRole={currentRole}
        />
      );
      continue;
    }

    elements.push(
      <MessageBubble
        key={message.id}
        message={message}
        isOwn={isOwn}
        isGrouped={isGrouped}
        isGroup={isGroup}
        isEditing={isEditing}
        editText={editText}
        setEditText={setEditText}
        onSaveEdit={onSaveEdit}
        onCancelEdit={onCancelEdit}
        currentUserId={currentUserId}
        onReply={() => onReply(message)}
        onForward={() => onForward?.(message)}
        onEdit={() => onEdit(message)}
        onDeleteForMe={() => onDeleteForMe(message.id)}
        onDeleteForEveryone={() => onDeleteForEveryone(message.id)}
        onAddReaction={onAddReaction}
        onRemoveReaction={onRemoveReaction}
        activeMessageId={activeMessageId}
        setActiveMessageId={setActiveMessageId}
      />
    );
  }

  return <div className="space-y-1">{elements}</div>;
}
